import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';
import { TimeField, fmt12, pad2 } from './TimeField';
import { CalendarClock, MapPin, MessageCircle } from 'lucide-react';

// ─── Scheduled changes to cruise times and the boarding point ─────────────────
//
// A change is entered WITH the date it starts. The standing rows in
// cruise_settings / ticket_settings stay as the base layer; a change overrides
// them from its effective_from date onward, and the next change overrides the
// one before it. Every reader (tickets, WhatsApp sends, the booking site, the
// marketing site) resolves the value in force on the CRUISE DATE through the
// database function cruise_details_on(), so nothing here touches the standing
// rows. Cancelling a change (cancelled_at) before it starts drops it from every
// lookup; a change that has already started cannot be cancelled here.
//
// After a change is saved, the guests already booked on or after its date are
// listed with a WhatsApp link so they can be told what changed.

// --- Date helpers -----------------------------------------------------------
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const isoToDmy = (iso) => { const [y, m, d] = String(iso || '').split('-'); return (y && m && d) ? `${d}-${m}-${y}` : String(iso || ''); };
const dmyToIso = (dmy) => { const [d, m, y] = String(dmy || '').split('-'); return (y && m && d) ? `${y}-${m}-${d}` : null; };
const dmyToNum = (dmy) => {
  const [d, m, y] = String(dmy || '').split('-').map(Number);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return 0;
  return y * 10000 + m * 100 + d;
};
const isoToNum = (iso) => Number(String(iso || '').replace(/-/g, '')) || 0;
const minutesOf = (hhmm) => { const [h, m] = String(hhmm || '').split(':').map(Number); return h * 60 + m; };

const digits10 = (raw) => { const d = String(raw || '').replace(/\D/g, ''); return d.length >= 10 ? d.slice(-10) : null; };

async function staffEmail() {
  const { data } = await supabase.auth.getUser();
  return data?.user?.email || null;
}

// --- Styles (match CruiseSettings) -----------------------------------------
const iStyle = {
  width: '100%', background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)',
  borderRadius: 8, padding: '9px 12px', fontSize: '0.88rem', color: NAVY,
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const lStyle = { fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-ink-secondary)', marginBottom: 5, display: 'block' };
const fg = { marginBottom: 14 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 };
const card = (maxWidth) => ({ background: '#fff', borderRadius: 14, padding: '22px 24px', maxWidth, width: '100%', boxShadow: '0 8px 32px rgba(10,22,40,0.22)', maxHeight: '90vh', overflowY: 'auto' });
const sectionLabel = { fontSize: '0.69rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' };
const primaryBtn = { flex: 1, background: GOLD, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' };
const ghostBtn = { flex: 1, background: 'none', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 0', fontWeight: 600, cursor: 'pointer', color: 'var(--color-ink-secondary)', fontSize: '0.88rem' };

// --- Affected bookings --------------------------------------------------------
// Every non-cancelled booking sailing on or after `fromIso` (optionally one
// cruise). bookings.date is DD-MM-YYYY text, so the query narrows to the next
// twelve months by month pattern and the exact date filter runs here.
async function loadAffectedBookings({ fromIso, cruiseName }) {
  const [y, m] = fromIso.split('-').map(Number);
  const patterns = [];
  for (let i = 0; i < 12; i++) {
    const idx = m - 1 + i;
    patterns.push(`date.like.*-${pad2((idx % 12) + 1)}-${y + Math.floor(idx / 12)}`);
  }
  let q = supabase
    .from('bookings')
    .select('id, booking_name, guest_phone, partner_name, partner_phone, date, cruise_type, deck, payment_status')
    .or(patterns.join(','))
    .or('payment_status.is.null,payment_status.neq.Cancelled');
  if (cruiseName) q = q.eq('cruise_type', cruiseName);
  const { data, error } = await q;
  if (error) throw error;
  const fromNum = isoToNum(fromIso);
  return (data || [])
    .filter((b) => dmyToNum(b.date) >= fromNum)
    .sort((a, b) => dmyToNum(a.date) - dmyToNum(b.date) || String(a.cruise_type).localeCompare(String(b.cruise_type)));
}

// The values in force for one (cruise, date) — what the guest's ticket should say.
async function detailsFor(cruiseName, dmy) {
  const { data } = await supabase.rpc('cruise_details_on', { p_cruise_name: cruiseName, p_date: dmyToIso(dmy) });
  return Array.isArray(data) && data[0] ? data[0] : null;
}

function AffectedBookingsModal({ title, subtitle, fromIso, cruiseName, buildMessage, onClose }) {
  const [rows, setRows]       = useState(null);
  const [details, setDetails] = useState({});
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await loadAffectedBookings({ fromIso, cruiseName });
        if (cancelled) return;
        setRows(list);
        // One lookup per distinct (cruise, date) so each message carries the
        // values in force on that guest's own sailing date.
        const keys = [...new Set(list.map((b) => `${b.cruise_type}|${b.date}`))];
        const found = {};
        await Promise.all(keys.map(async (k) => {
          const [c, d] = k.split('|');
          found[k] = await detailsFor(c, d);
        }));
        if (!cancelled) setDetails(found);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load bookings.');
      }
    })();
    return () => { cancelled = true; };
  }, [fromIso, cruiseName]);

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={overlay}>
      <div style={card(640)}>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 16 }}>{subtitle}</div>

        {error && <div className="toast toast-error" style={{ marginBottom: 12 }}>{error}</div>}
        {!rows && !error && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: '0.88rem' }}>Loading bookings…</div>}
        {rows && rows.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-ink-muted)', fontSize: '0.88rem' }}>
            No one is booked on or after this date yet. Nothing to send.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ ...sectionLabel, marginBottom: 2 }}>{rows.length} booking{rows.length === 1 ? '' : 's'} to inform</div>
            {rows.map((b) => {
              const phone = digits10(b.guest_phone) || digits10(b.partner_phone);
              const via = digits10(b.guest_phone) ? 'guest' : (digits10(b.partner_phone) ? `partner${b.partner_name ? ` · ${b.partner_name}` : ''}` : null);
              const d = details[`${b.cruise_type}|${b.date}`];
              const text = buildMessage(b, d);
              const href = phone ? `https://wa.me/91${phone}?text=${encodeURIComponent(text)}` : null;
              return (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-canvas)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.booking_name || 'Guest'}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--color-ink-muted)', marginTop: 2 }}>
                      {b.date} · {b.cruise_type}{b.deck ? ` · ${b.deck}` : ''}{via ? ` · ${via}` : ''}
                    </div>
                  </div>
                  {href ? (
                    <a className="btn btn-sm btn-whatsapp" href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', flexShrink: 0 }}>
                      <MessageCircle size={14} strokeWidth={2} aria-hidden="true" /> WhatsApp
                    </a>
                  ) : (
                    <span style={{ fontSize: '0.74rem', color: '#dc2626', fontWeight: 600, flexShrink: 0 }}>No number</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={ghostBtn}>Close</button>
        </div>
      </div>
    </div>
  );
}

// --- Cruise times ------------------------------------------------------------
function ScheduleTimesModal({ cruise, current, onSaved, onClose }) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [departure, setDeparture]         = useState(current?.departure_time || '');
  const [end, setEnd]                     = useState(current?.end_time || '');
  const [reporting, setReporting]         = useState(current?.reporting_time || '');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');

  const handleSave = async () => {
    setError('');
    if (!effectiveFrom) { setError('Pick the date the new times start.'); return; }
    if (isoToNum(effectiveFrom) < isoToNum(todayIso())) { setError('The start date cannot be in the past.'); return; }
    // All three together — a reporting time without its departure time is a
    // mistake waiting to happen.
    if (!departure || !end || !reporting) { setError('Enter all three times: reporting, departure and end.'); return; }
    if (minutesOf(reporting) > minutesOf(departure)) { setError('Reporting time must be before departure.'); return; }
    if (minutesOf(end) <= minutesOf(departure)) { setError('End time must be after departure.'); return; }
    setSaving(true);
    const { data, error: err } = await supabase
      .from('cruise_schedule_changes')
      .insert({
        cruise_id: cruise.id,
        effective_from: effectiveFrom,
        departure_time: departure,
        end_time: end,
        reporting_time: reporting,
        created_by: await staffEmail(),
      })
      .select('*')
      .single();
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved(data);
  };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={overlay}>
      <div style={card(440)}>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>Schedule a time change</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 16 }}>
          {cruise.cruise_name}. The new times apply to every sailing from the start date; sailings before it keep the current times.
        </div>

        <div style={fg}>
          <label style={lStyle}>Starts on *</label>
          <input type="date" min={todayIso()} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} style={iStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
          <div style={fg}>
            <label style={lStyle}>Reporting *</label>
            <TimeField value={reporting} onChange={setReporting} />
          </div>
          <div style={fg}>
            <label style={lStyle}>Departure *</label>
            <TimeField value={departure} onChange={setDeparture} />
          </div>
          <div style={fg}>
            <label style={lStyle}>End *</label>
            <TimeField value={end} onChange={setEnd} />
          </div>
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '-4px 0 12px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : 'Schedule'}</button>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const timesLine = (c) => `Report ${fmt12(c.reporting_time)} · ${fmt12(c.departure_time)} – ${fmt12(c.end_time)}`;

export function CruiseTimesSchedule({ cruise, current, onChanged }) {
  const [changes, setChanges]     = useState([]);
  const [modal, setModal]         = useState(false);
  const [affected, setAffected]   = useState(null); // the change just saved
  const [cancelling, setCancelling] = useState(null); // change id awaiting confirmation
  const [error, setError]         = useState('');

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('cruise_schedule_changes')
      .select('*')
      .eq('cruise_id', cruise.id)
      .is('cancelled_at', null)
      .order('effective_from', { ascending: false })
      .order('created_at', { ascending: false });
    if (err) { setError(err.message); return; }
    setChanges(data || []);
  }, [cruise.id]);

  useEffect(() => { load(); }, [load]);

  const today = todayIso();
  const inForce = changes.find((c) => c.effective_from <= today) || null;
  const pending = changes.filter((c) => c.effective_from > today).sort((a, b) => a.effective_from.localeCompare(b.effective_from));

  const handleCancel = async (c) => {
    const { error: err } = await supabase.from('cruise_schedule_changes').update({ cancelled_at: new Date().toISOString() }).eq('id', c.id);
    setCancelling(null);
    if (err) { setError(err.message); return; }
    await load();
    onChanged?.();
  };

  const buildMessage = (b, d) => {
    const name = b.booking_name || 'Guest';
    const t = d || {};
    const parts = [`Hello ${name}, an update from Alfresco Grand for your ${b.cruise_type} on ${b.date}.`];
    if (t.reporting_time) parts.push(`Please report by ${fmt12(t.reporting_time)}.`);
    if (t.departure_time && t.end_time) parts.push(`The cruise departs at ${fmt12(t.departure_time)} and ends at ${fmt12(t.end_time)}.`);
    if (t.boarding_point) parts.push(`Boarding is at ${t.boarding_point}.`);
    parts.push('Your existing ticket remains valid. See you on board.');
    return parts.join(' ');
  };

  return (
    <div style={{ borderTop: '1px solid var(--color-border-subtle)', padding: '12px 20px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...sectionLabel }}>
          <CalendarClock size={13} strokeWidth={1.75} aria-hidden="true" /> Scheduled time changes
        </div>
        <button className="btn btn-sm btn-outline" onClick={() => setModal(true)}>Schedule a change</button>
      </div>

      {error && <div className="toast toast-error" style={{ marginTop: 10 }}>{error}</div>}

      {inForce && (
        <div style={{ fontSize: '0.76rem', color: 'var(--color-ink-secondary)', marginTop: 8 }}>
          In force since {isoToDmy(inForce.effective_from)}: {timesLine(inForce)}
        </div>
      )}

      {pending.length === 0 ? (
        <div style={{ fontSize: '0.76rem', color: 'var(--color-ink-faint)', marginTop: 8 }}>Nothing scheduled.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {pending.map((c) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-canvas)' }}>
              <div style={{ flex: 1, minWidth: 200, fontSize: '0.82rem', color: NAVY }}>
                <strong>From {isoToDmy(c.effective_from)}:</strong> {timesLine(c)}
              </div>
              {cancelling === c.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--color-ink-secondary)' }}>
                  Cancel this change?
                  <button className="btn btn-sm btn-danger" onClick={() => handleCancel(c)}>Yes, cancel</button>
                  <button className="btn btn-sm btn-outline" onClick={() => setCancelling(null)}>Keep</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => setAffected(c)}>Who to inform</button>
                  <button className="btn btn-sm btn-outline" onClick={() => setCancelling(c.id)}>Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ScheduleTimesModal
          cruise={cruise}
          current={current}
          onClose={() => setModal(false)}
          onSaved={async (row) => { setModal(false); await load(); onChanged?.(); setAffected(row); }}
        />
      )}
      {affected && (
        <AffectedBookingsModal
          title="Guests to inform"
          subtitle={`${cruise.cruise_name} from ${isoToDmy(affected.effective_from)}: ${timesLine(affected)}. Each link opens WhatsApp with the new details for that guest's own sailing date.`}
          fromIso={affected.effective_from}
          cruiseName={cruise.cruise_name}
          buildMessage={buildMessage}
          onClose={() => setAffected(null)}
        />
      )}
    </div>
  );
}

// --- Boarding point ------------------------------------------------------------
function ScheduleBoardingModal({ current, onSaved, onClose }) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [point, setPoint]                 = useState('');
  const [mapsUrl, setMapsUrl]             = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');

  const handleSave = async () => {
    setError('');
    if (!effectiveFrom) { setError('Pick the date the new boarding point starts.'); return; }
    if (isoToNum(effectiveFrom) < isoToNum(todayIso())) { setError('The start date cannot be in the past.'); return; }
    if (!point.trim()) { setError('Enter the new boarding point.'); return; }
    if (mapsUrl.trim() && !/^https?:\/\//i.test(mapsUrl.trim())) { setError('The map link must start with http:// or https://.'); return; }
    setSaving(true);
    const { data, error: err } = await supabase
      .from('boarding_point_changes')
      .insert({
        effective_from: effectiveFrom,
        boarding_point: point.trim(),
        boarding_maps_url: mapsUrl.trim() || null,
        created_by: await staffEmail(),
      })
      .select('*')
      .single();
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved(data);
  };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={overlay}>
      <div style={card(440)}>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>Schedule a boarding point change</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 16 }}>
          One boarding point for all cruises. It applies to every sailing from the start date; sailings before it keep the current point{current ? ` (${current})` : ''}.
        </div>

        <div style={fg}>
          <label style={lStyle}>Starts on *</label>
          <input type="date" min={todayIso()} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} style={iStyle} />
        </div>
        <div style={fg}>
          <label style={lStyle}>New boarding point *</label>
          <textarea rows={2} value={point} onChange={(e) => setPoint(e.target.value)} style={{ ...iStyle, resize: 'vertical', lineHeight: 1.5 }} placeholder="e.g. Gateway of Guwahati terminal, Panbazar, Guwahati" />
        </div>
        <div style={fg}>
          <label style={lStyle}>Google Maps link <span style={{ fontWeight: 400 }}>(optional)</span></label>
          <input value={mapsUrl} onChange={(e) => setMapsUrl(e.target.value)} style={iStyle} placeholder="https://maps.app.goo.gl/…" />
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '-4px 0 12px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : 'Schedule'}</button>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function BoardingPointSchedule({ current, onChanged }) {
  const [changes, setChanges]       = useState([]);
  const [modal, setModal]           = useState(false);
  const [affected, setAffected]     = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('boarding_point_changes')
      .select('*')
      .is('cancelled_at', null)
      .order('effective_from', { ascending: false })
      .order('created_at', { ascending: false });
    if (err) { setError(err.message); return; }
    setChanges(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = todayIso();
  const inForce = changes.find((c) => c.effective_from <= today) || null;
  const pending = changes.filter((c) => c.effective_from > today).sort((a, b) => a.effective_from.localeCompare(b.effective_from));

  const handleCancel = async (c) => {
    const { error: err } = await supabase.from('boarding_point_changes').update({ cancelled_at: new Date().toISOString() }).eq('id', c.id);
    setCancelling(null);
    if (err) { setError(err.message); return; }
    await load();
    onChanged?.();
  };

  const buildMessage = (b, d) => {
    const name = b.booking_name || 'Guest';
    const t = d || {};
    const parts = [`Hello ${name}, an update from Alfresco Grand for your ${b.cruise_type} on ${b.date}.`];
    if (t.boarding_point) parts.push(`Boarding is now at ${t.boarding_point}.`);
    if (t.boarding_maps_url) parts.push(`Map: ${t.boarding_maps_url}`);
    if (t.reporting_time) parts.push(`Please report by ${fmt12(t.reporting_time)}.`);
    parts.push('Your existing ticket remains valid. See you on board.');
    return parts.join(' ');
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...sectionLabel }}>
          <MapPin size={13} strokeWidth={1.75} aria-hidden="true" /> Scheduled boarding changes
        </div>
        <button className="btn btn-sm btn-outline" onClick={() => setModal(true)}>Schedule a change</button>
      </div>

      {error && <div className="toast toast-error" style={{ marginTop: 10 }}>{error}</div>}

      {inForce && (
        <div style={{ fontSize: '0.76rem', color: 'var(--color-ink-secondary)', marginTop: 8 }}>
          In force since {isoToDmy(inForce.effective_from)}: {inForce.boarding_point}
        </div>
      )}

      {pending.length === 0 ? (
        <div style={{ fontSize: '0.76rem', color: 'var(--color-ink-faint)', marginTop: 8 }}>Nothing scheduled.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {pending.map((c) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-canvas)' }}>
              <div style={{ flex: 1, minWidth: 180, fontSize: '0.82rem', color: NAVY }}>
                <strong>From {isoToDmy(c.effective_from)}:</strong> {c.boarding_point}
              </div>
              {cancelling === c.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--color-ink-secondary)' }}>
                  Cancel this change?
                  <button className="btn btn-sm btn-danger" onClick={() => handleCancel(c)}>Yes, cancel</button>
                  <button className="btn btn-sm btn-outline" onClick={() => setCancelling(null)}>Keep</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => setAffected(c)}>Who to inform</button>
                  <button className="btn btn-sm btn-outline" onClick={() => setCancelling(c.id)}>Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ScheduleBoardingModal
          current={current}
          onClose={() => setModal(false)}
          onSaved={async (row) => { setModal(false); await load(); onChanged?.(); setAffected(row); }}
        />
      )}
      {affected && (
        <AffectedBookingsModal
          title="Guests to inform"
          subtitle={`Boarding from ${isoToDmy(affected.effective_from)}: ${affected.boarding_point}. Each link opens WhatsApp with the new details for that guest's own sailing date.`}
          fromIso={affected.effective_from}
          cruiseName={null}
          buildMessage={buildMessage}
          onClose={() => setAffected(null)}
        />
      )}
    </div>
  );
}
